import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { SessionPayload } from '@wr/shared';
import { ApiError, errorMessage, post } from '../api.js';
import { Checkbox, Field, Notice } from '../components/ui.js';
import { useLoaded } from '../session.js';
import { AuthLayout } from './AuthLayout.js';

/** Second step: the 6-digit code of the authenticator app, or a recovery code. */
export function Code() {
  const { rules, setSession } = useLoaded();
  const navigate = useNavigate();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = useRecovery ? { recoveryCode: code, trustDevice } : { code, trustDevice };
      setSession(await post<SessionPayload>('/auth/code', body));
    } catch (err) {
      setCode('');
      if (err instanceof ApiError && err.code === 'invalid_credentials') {
        setSession({ state: 'signed_out' });
        navigate('/sign-in', {
          replace: true,
          state: {
            notice: `Too many wrong codes: the account is locked for ${rules.lockout.lockoutMinutes} minutes.`,
          },
        });
        return;
      }
      if (err instanceof ApiError && err.code === 'unauthenticated') {
        setSession({ state: 'signed_out' });
        return;
      }
      setError(
        err instanceof ApiError && err.code === 'invalid_code'
          ? useRecovery
            ? 'This recovery code is not valid or was already used.'
            : 'This code is not valid. Enter the code your app shows now.'
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  async function back() {
    await post('/auth/sign-out').catch(() => undefined);
    setSession({ state: 'signed_out' });
  }

  return (
    <AuthLayout title="Two-step sign-in">
      <p className="muted">
        {useRecovery
          ? 'Enter one of the recovery codes you saved. Each code works once.'
          : 'Open your authenticator app and enter the 6-digit code for the WR Sales Portal.'}
      </p>
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={(e) => void submit(e)} className="vstack">
        {useRecovery ? (
          <Field
            key="recovery"
            label="Recovery code"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        ) : (
          <Field
            key="code"
            label="Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]{6,7}"
            maxLength={7}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        )}
        {rules.trustedDeviceDays > 0 && (
          <Checkbox checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)}>
            Trust this device for {rules.trustedDeviceDays} days: no code asked here, only your
            password. Never on a shared device.
          </Checkbox>
        )}
        <button type="submit" className="btn primary block" disabled={busy}>
          Continue
        </button>
      </form>
      <div className="actions">
        <button
          type="button"
          className="linkbtn"
          onClick={() => {
            setUseRecovery(!useRecovery);
            setCode('');
            setError(null);
          }}
        >
          {useRecovery ? 'Use the app code instead' : 'I do not have my phone: use a recovery code'}
        </button>
        <button type="button" className="linkbtn" onClick={() => void back()}>
          Back to sign in
        </button>
      </div>
    </AuthLayout>
  );
}
